/**
 * Story 049 — `kpi-open-inspections` widget body.
 *
 * Reads `GET /api/lyse/v1/data-acquisitions?state=OPEN&pageSize=1` per
 * story §"Widget set" and uses the response's `total` for the headline
 * number. (The post-051 path is `/data-acquisitions`; the canonical
 * lyse axios baseURL is `/api/lyse/v1`.)
 *
 * UX brief §3.2 — single big number in `display-md`, optional
 * delta-vs-last-week pill below. The delta needs a second fetch
 * (a week-old `createdSince` cut-off) to compare; for MVP we render
 * just the headline number plus a placeholder caption — the delta
 * variant ships in a follow-up to keep the cross-fetch surface area
 * small. Story §"Open questions" anticipates this kind of split.
 */
import { useMemo, type ReactElement } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { typography } from "@aeriosiq/ui";
import { WidgetRetryPill } from "./WidgetRetryPill.js";

interface OpenInspectionsResponse {
  total?: number;
  items?: unknown[];
}

export function KpiOpenInspections(): ReactElement {
  const api = useApi();
  const query = useQuery<OpenInspectionsResponse>({
    queryKey: ["dashboard", "kpi", "open-inspections"],
    queryFn: async () => {
      const response = await api.get<OpenInspectionsResponse>(
        "/data-acquisitions",
        { params: { state: "OPEN", pageSize: 1 } },
      );
      return response.data;
    },
    staleTime: 60_000,
    retry: false,
  });

  const total = useMemo(() => {
    if (typeof query.data?.total === "number") return query.data.total;
    return 0;
  }, [query.data?.total]);

  if (query.isPending) {
    return (
      <Body>
        <Skeleton variant="rounded" width="50%" height={48} />
        <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
      </Body>
    );
  }

  if (query.isError) {
    return (
      <Body>
        <WidgetRetryPill
          message="Couldn't load Open Inspections."
          onRetry={() => {
            void query.refetch();
          }}
        />
      </Body>
    );
  }

  return (
    <Body>
      <Typography
        component="div"
        sx={{
          fontSize: typography.size["display-md"],
          fontWeight: typography.weight.semibold,
          letterSpacing: typography.tracking["display-md"],
          fontVariantNumeric: "tabular-nums",
          color: "text.primary",
          lineHeight: 1.05,
        }}
      >
        {total}
      </Typography>
      <Typography
        variant="caption"
        component="div"
        sx={{
          mt: 1,
          color: "text.secondary",
          fontSize: typography.size["caption-lg"],
        }}
      >
        {total === 0
          ? "No open inspections yet — they'll appear here once your team creates one."
          : "across all open data acquisitions"}
      </Typography>
    </Body>
  );
}

function Body({ children }: { children: React.ReactNode }): ReactElement {
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
      }}
    >
      {children}
    </Box>
  );
}
